import styled from 'styled-components';
import { colors, spacing, animations, borderRadius, typography, shadows } from './designTokens';

// 메인 컨테이너
export const DashboardContainer = styled.div`
  display: block;
  min-height: 100vh;
  background: linear-gradient(135deg, ${colors.semantic.background.secondary} 0%, ${colors.semantic.background.tertiary} 100%);
  position: relative;
  
  /* 배경 패턴 */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
`;

// 사이드바
export const Sidebar = styled.div`
  width: 240px;
  background-color: ${colors.semantic.background.primary};
  border-right: 1px solid ${colors.semantic.border.primary};
  padding: ${spacing[6]} 0;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

// 사이드바 로고
export const SidebarLogo = styled.div`
  padding: 0 ${spacing[6]} ${spacing[8]};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  margin-bottom: ${spacing[6]};
  
  h1 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.secondary[800]};
    margin: 0;
  }
  
  span {
    font-size: ${typography.fontSize.sm};
    color: ${colors.secondary[500]};
  }
`;

// 사이드바 메뉴
export const SidebarMenu = styled.nav`
  padding: 0 ${spacing[4]};
`;

// 메뉴 아이템
export const MenuItem = styled.div`
  margin-bottom: ${spacing[1]};
  border-radius: ${borderRadius.lg};
  transition: all 0.2s;
  
  a, button {
    display: flex;
    align-items: center;
    padding: ${spacing[3]} ${spacing[4]};
    width: 100%;
    border-radius: ${borderRadius.lg};
    cursor: pointer;
    transition: all 0.2s;
    background-color: transparent;
    color: ${colors.secondary[500]};
    text-decoration: none;
    border: none;
    font-family: inherit;
    font-size: inherit;
    
    &:hover {
      background-color: ${colors.secondary[100]};
      color: ${colors.primary[800]};
    }
    
    &.active {
      background-color: ${colors.secondary[100]};
      color: ${colors.primary[800]};
    }
    
    svg {
      margin-right: 12px;
      width: 20px;
      height: 20px;
    }
    
    span {
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

// 메인 콘텐츠
export const MainContent = styled.div<{ $sidebarCollapsed?: boolean }>`
  margin-left: ${({ $sidebarCollapsed }) => $sidebarCollapsed ? '0px' : '140px'}; /* 사이드바 상태에 따른 동적 조정 */
  padding: ${spacing[6]};
  position: relative;
  z-index: 1;
  min-height: 100vh;
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  
  /* 사이드바와의 시각적 분리를 위한 미세 조정 */
  &::before {
    content: '';
    position: absolute;
    left: -1px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(180deg, transparent 0%, ${colors.semantic.border.primary} 20%, ${colors.semantic.border.primary} 80%, transparent 100%);
    opacity: 0.3;
  }
  
  /* 태블릿 (768px - 1024px) */
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-left: 80px; /* 접힌 사이드바 너비 */
    padding: ${spacing[5]};
  }
  
  /* 모바일 (768px 이하) */
  @media (max-width: 767px) {
    margin-left: 0;
    padding: ${spacing[4]} ${spacing[3]};
    padding-top: calc(60px + ${spacing[4]}); /* 모바일 헤더 높이 + 패딩 */
    min-height: calc(100vh - 80px); /* 하단 네비게이션 공간 확보 */
  }
  
  /* 매우 작은 모바일 (480px 이하) */
  @media (max-width: 480px) {
    padding: ${spacing[3]} ${spacing[2]};
  }
`;

// 헤더
export const Header = styled.div`
  margin-bottom: ${spacing[8]};
  
  h1 {
    font-size: ${typography.fontSize['4xl']};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.secondary[800]};
    margin: 0 0 ${spacing[2]} 0;
  }
  
  p {
    color: ${colors.secondary[500]};
    margin: 0;
    font-size: ${typography.fontSize.sm};
  }
`;

// 통계 카드 그리드 - 사이드바 상태에 최적화된 반응형 레이아웃
export const StatsGrid = styled.div<{ $sidebarCollapsed?: boolean }>`
  display: grid;
  gap: ${({ $sidebarCollapsed }) => $sidebarCollapsed ? '20px' : '24px'};
  margin-bottom: 32px;
  
  /* 모바일: 1열 */
  grid-template-columns: 1fr;
  
  /* 작은 태블릿: 2열 */
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  /* 사이드바 접힌 상태: 3열로 최적화 */
  @media (min-width: 1024px) {
    grid-template-columns: ${({ $sidebarCollapsed }) =>
    $sidebarCollapsed ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)'};
  }
  
  /* 큰 화면: 사이드바 상태에 따른 최적화 */
  @media (min-width: 1400px) {
    grid-template-columns: ${({ $sidebarCollapsed }) =>
    $sidebarCollapsed ? 'repeat(4, 1fr)' : 'repeat(4, 1fr)'};
    max-width: ${({ $sidebarCollapsed }) =>
    $sidebarCollapsed ? '1000px' : '1200px'};
    margin: 0 auto 32px auto;
  }
  
  /* 초대형 화면: 더 많은 공간 활용 */
  @media (min-width: 1600px) {
    grid-template-columns: ${({ $sidebarCollapsed }) =>
    $sidebarCollapsed ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)'};
    max-width: ${({ $sidebarCollapsed }) =>
    $sidebarCollapsed ? '1400px' : '1200px'};
  }
`;

// 통계 카드 - 개선된 디자인
export const StatsCard = styled.div<{ $clickable?: boolean; $isActive?: boolean }>`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius['2xl']};
  padding: ${spacing[6]};
  border: 1px solid
    ${props => (props.$isActive ? colors.primary[600] : colors.semantic.border.primary)};
  box-shadow: ${props => (props.$isActive ? shadows.primary : shadows.sm)};
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => (props.$isActive ? 'translateY(-4px)' : 'none')};
  position: relative;
  overflow: hidden;

  ${props =>
    props.$isActive &&
    `
    background: linear-gradient(135deg, ${colors.semantic.background.primary} 0%, ${colors.secondary[50]} 100%);
    border-color: ${colors.primary[500]};
  `}

  &:hover {
    ${props =>
      props.$clickable &&
      `
      transform: translateY(-4px);
      box-shadow: ${shadows.lg};
      border-color: ${props.$isActive ? colors.primary[700] : colors.primary[500]};
      background: linear-gradient(135deg, ${colors.semantic.background.primary} 0%, ${colors.secondary[50]} 100%);
    `}
  }

  @media (max-width: 640px) {
    padding: ${spacing[5]};
    border-radius: ${borderRadius.xl};
  }

  h3 {
    font-size: ${typography.fontSize.sm};
    color: ${colors.secondary[500]};
    margin: 0 0 ${spacing[3]} 0;
    font-weight: ${typography.fontWeight.medium};
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 640px) {
      font-size: 13px;
      margin-bottom: ${spacing[2]};
    }
  }

  .value {
    font-size: 32px;
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.secondary[800]};
    margin-bottom: ${spacing[3]};
    line-height: ${typography.lineHeight.tight};

    @media (max-width: 640px) {
      font-size: 28px;
      margin-bottom: ${spacing[2]};
    }

    @media (max-width: 480px) {
      font-size: 24px;
    }
  }

  .change {
    display: flex;
    align-items: center;
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};

    @media (max-width: 640px) {
      font-size: 13px;
    }

    .icon {
      margin-right: 6px;
      font-size: ${typography.fontSize.base};
    }
  }
`;

// 차트 섹션
export const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  /* 데스크톱에서는 2열 */
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
  
  /* 큰 화면에서는 3열도 가능 */
  @media (min-width: 1400px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

// 키워드 섹션 - 사이드바 상태에 최적화된 가로 배치
export const KeywordSection = styled.div<{ $sidebarCollapsed?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ $sidebarCollapsed }) => $sidebarCollapsed ? '20px' : '24px'};
  margin-bottom: 32px;
  
  /* 태블릿 이상에서는 2열 가로 배치 */
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  /* 사이드바 접힌 상태에서 더 컴팩트한 레이아웃 */
  @media (min-width: 1024px) {
    gap: ${({ $sidebarCollapsed }) => $sidebarCollapsed ? '16px' : '24px'};
  }
  
  /* 큰 화면에서 최대 너비 제한 */
  @media (min-width: 1400px) {
    max-width: ${({ $sidebarCollapsed }) =>
    $sidebarCollapsed ? '1000px' : '1200px'};
    margin: 0 auto 32px auto;
  }
`;

// 차트 카드
export const ChartCard = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.sm};

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.secondary[800]};
    margin: 0 0 ${spacing[6]} 0;
  }

  .chart-value {
    text-align: center;
    margin-bottom: ${spacing[6]};

    .main-value {
      font-size: ${typography.fontSize['4xl']};
      font-weight: ${typography.fontWeight.bold};
      color: ${colors.secondary[800]};
    }

    .unit {
      font-size: ${typography.fontSize.lg};
      color: ${colors.secondary[500]};
      margin-left: ${spacing[1]};
    }
  }
`;

// 테이블 섹션
export const TableSection = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.sm};
  overflow: hidden;
`;

// 테이블 헤더
export const TableHeader = styled.div`
  padding: ${spacing[6]};
  border-bottom: 1px solid ${colors.semantic.border.primary};

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.secondary[800]};
    margin: 0;
  }
`;

// 테이블
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    background-color: ${colors.secondary[50]};
    padding: ${spacing[4]} ${spacing[6]};
    text-align: left;
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.semantic.text.secondary};
    border-bottom: 1px solid ${colors.semantic.border.primary};
  }

  td {
    padding: ${spacing[4]} ${spacing[6]};
    font-size: ${typography.fontSize.sm};
    color: ${colors.secondary[800]};
    border-bottom: 1px solid ${colors.secondary[100]};
  }

  tr:hover {
    background-color: ${colors.secondary[50]};
  }
`;

// 페이지네이션
export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing[6]};
  gap: ${spacing[2]};

  button {
    padding: ${spacing[2]} ${spacing[3]};
    border: 1px solid ${colors.semantic.border.primary};
    background: ${colors.semantic.background.primary};
    border-radius: ${borderRadius.md};
    cursor: pointer;
    font-size: ${typography.fontSize.sm};
    color: ${colors.secondary[500]};
    transition: all 0.2s;

    &:hover {
      background-color: ${colors.secondary[100]};
      border-color: ${colors.semantic.border.secondary};
    }

    &.active {
      background-color: ${colors.primary[500]};
      border-color: ${colors.primary[500]};
      color: ${colors.semantic.text.inverse};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

// 로딩 스피너
export const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid ${colors.neutral[100]};
    border-top: 4px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// 에러 메시지
export const ErrorMessage = styled.div`
  background-color: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[600]};
  padding: ${spacing[4]};
  border-radius: ${borderRadius.lg};
  margin: ${spacing[4]} 0;
  text-align: center;
`; 